/**
 * FrenteSituacaoChip — o chip da situação de uma frente de obra (#365, D17).
 *
 * Usa o mesmo mapa de tons do `ValidityChip` (sucesso, aviso, erro, informação), mas com os
 * códigos da frente: os de validade não descrevem uma frente, e estendê-los contaminaria o chip
 * comum. Texto, ícone e cor andam juntos, então nada é dito só pela cor. Os dias de atraso vêm
 * do servidor; o chip só os escreve.
 */
import React from 'react';
import { Chip, type SxProps, type Theme } from '@mui/material';
import {
  Block as ClosedIcon,
  CheckCircle as OkIcon,
  ErrorOutline as BadIcon,
  HourglassEmpty as PendingIcon,
  WarningAmber as WarnIcon,
} from '@mui/icons-material';

import type { WorkFrontSituation } from '../../../types/workFront';

type Tone = 'success' | 'warning' | 'error' | 'info';

export interface FrenteSituacaoChipProps {
  situation: WorkFrontSituation;
  /** Dias da etapa mais atrasada; só entra no texto de "Etapa atrasada". */
  lateDays?: number;
}

const TONE_ICONS: Record<Tone, React.ReactElement> = {
  success: <OkIcon />,
  warning: <WarnIcon />,
  error: <BadIcon />,
  info: <PendingIcon />,
};

const TONE_BY_SITUATION: Record<WorkFrontSituation, Tone> = {
  ENCERRADA: 'info',
  ETAPA_ATRASADA: 'error',
  COM_PENDENCIA: 'warning',
  NAO_INICIADA: 'info',
  NO_PRAZO: 'success',
};

const LABEL_BY_SITUATION: Record<WorkFrontSituation, string> = {
  ENCERRADA: 'Encerrada',
  ETAPA_ATRASADA: 'Etapa atrasada',
  COM_PENDENCIA: 'Com pendência',
  NAO_INICIADA: 'Não iniciada',
  NO_PRAZO: 'No prazo',
};

const pluralDays = (count: number): string => `${count} ${count === 1 ? 'dia' : 'dias'}`;

/** Texto e borda no tom escuro da paleta (o `main` fica abaixo de 4,5:1 sobre o branco). */
const readableChipSx =
  (tone: Tone): SxProps<Theme> =>
  (theme) => ({
    fontSize: '1rem',
    fontWeight: 500,
    color: theme.palette[tone].dark,
    borderColor: theme.palette[tone].dark,
    '& .MuiChip-icon': { color: 'inherit' },
    ...theme.applyStyles('dark', {
      color: theme.palette[tone].light,
      borderColor: theme.palette[tone].light,
    }),
  });

export const FrenteSituacaoChip: React.FC<FrenteSituacaoChipProps> = ({ situation, lateDays }) => {
  const tone = TONE_BY_SITUATION[situation];
  const base = LABEL_BY_SITUATION[situation];
  const label = situation === 'ETAPA_ATRASADA' && lateDays ? `${base} · ${pluralDays(lateDays)}` : base;
  const icon = situation === 'ENCERRADA' ? <ClosedIcon /> : TONE_ICONS[tone];

  return <Chip size="small" variant="outlined" icon={icon} label={label} sx={readableChipSx(tone)} />;
};

export default FrenteSituacaoChip;
